import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormMultiOptionsSelector from "@babackapp/react-hook-forms-components/form-multi-options-selector";
import FormPasswordInput from "@babackapp/react-hook-forms-components/form-password-input";
import FormSaveButton from "@babackapp/react-hook-forms-components/form-save-button";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { FormField } from "@babackapp/ui/components/form";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@babackapp/ui/components/tabs";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const formSchema = z.object({
  url: z.string().url(),
  secret: z.string(),
  subscriptions: z.array(z.string()),
});

type WebhookFormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: WebhookFormValues = {
  url: "",
  secret: "",
  subscriptions: [],
};

type WebhookFormProps = {
  onSubmit: (data: WebhookFormValues) => void;
  onRemove?: () => void;
  defaultValues?: WebhookFormValues;
};

export default function WebhookForm({
  onRemove,
  onSubmit,
  defaultValues = DEFAULT_VALUES,
}: WebhookFormProps) {
  const [currentTab, setCurrentTab] = useState("details");
  const t = useTranslation();
  const form = useForm<WebhookFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const isFirstTabError =
    form.formState.errors.url || form.formState.errors.secret;

  useEffect(() => {
    if (isFirstTabError) {
      setCurrentTab("details");
    }
  }, [isFirstTabError]);

  const _onSubmit = form.handleSubmit(onSubmit);

  const options = useMemo(
    () =>
      EVENTS_KEYS.map((key) => ({
        key,
        label: t(`settings.integrations.webhooks.${key}` as Translation, {
          keySeparator: ".",
          nsSeparator: ".",
        }),
      })),
    [t],
  );

  const eventsLength = useWatch({
    control: form.control,
    name: "subscriptions",
    defaultValue: defaultValues.subscriptions ?? [],
  }).length;

  return (
    <FormProvider {...form}>
      <form onSubmit={_onSubmit} className="flex flex-col justify-start gap-8">
        <Tabs
          onValueChange={(tab) => setCurrentTab(tab)}
          value={currentTab}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="details">
              {t("settings.integrations.webhooks.form.tabs.details")}
            </TabsTrigger>
            <TabsTrigger value="events" className="gap-2 flex items-center">
              {t("settings.integrations.webhooks.form.tabs.events")}
              <span className="text-xs px-2 bg-purple-600 rounded-full text-white text-muted-foreground">
                {eventsLength}
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="flex flex-col gap-8">
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormInput
                  label={t(
                    "settings.integrations.webhooks.form.fields.url.label",
                  )}
                  description={t(
                    "settings.integrations.webhooks.form.fields.url.description",
                  )}
                  {...field}
                />
              )}
            />

            <FormField
              control={form.control}
              name="secret"
              render={({ field }) => (
                <FormPasswordInput
                  label={t(
                    "settings.integrations.webhooks.form.fields.secret.label",
                  )}
                  description={t(
                    "settings.integrations.webhooks.form.fields.secret.description",
                  )}
                  {...field}
                />
              )}
            />
          </TabsContent>

          <TabsContent value="events">
            <FormField
              control={form.control}
              name="subscriptions"
              render={({ field }) => (
                <FormMultiOptionsSelector
                  label={t(
                    "settings.integrations.webhooks.form.fields.events.label",
                  )}
                  description={t(
                    "settings.integrations.webhooks.form.fields.events.description",
                  )}
                  values={field.value ?? []}
                  onChange={field.onChange}
                  options={options}
                />
              )}
            />
          </TabsContent>
        </Tabs>

        <div className="flex gap-2 justify-center items-center">
          {onRemove && (
            <Button
              variant="destructive-outline"
              type="button"
              onClick={onRemove}
            >
              {t("misc.delete")}
            </Button>
          )}
          <FormSaveButton />
        </div>
      </form>
    </FormProvider>
  );
}

export const EVENTS_KEYS = [
  "returnRequest:returnRequest:create",
  "returnRequest:returnRequest:cancel",
  "returnRequest:returnRequest:approve",
  "returnRequest:returnRequest:update",
  "returnRequest:logistician:approve",
  "returnRequest:merchant:approve",
  "returnRequest:discount:create",
];
